import React, { useState, useEffect, useMemo } from 'react';
import { bindActionCreators } from 'redux';
import { connect, useSelector } from 'react-redux';
import TransactionSummaryComponent from './TransactionSummaryComponent';

import * as dashboardActions from '../../redux/actions/dashboard';
import * as plaidActions from '../../redux/actions/plaid';
import store from '../../redux/store/store';
import { retrieveBankAccountTransactionsIfNecessary } from "../../redux/actions/plaid";
import Transaction from "../../models/Transaction.jsx";
import TransactionsToolbar from "./transaction/TransactionsToolbar.jsx";
import ProgressIndeterminate from "../elements/ProgressIndeterminate.jsx";
import CircleLoader from "./CircleLoader.jsx";
import NoResults from "../elements/NoResults.jsx";
import plaidTransactionsAreLoadingOnDate from "redux/reducerUtilities/plaidTransactionsAreLoadingOnDate";
import findRefunds from "utilities/findRefunds";
import searchTransactions from "utilities/transactionFinder/transactionSearch";
import { setTransactionSearchSort } from "../../redux/actions/transactions.js";

/** Search results render this many at a time; "Show more" adds as many again. */
const SEARCH_PAGE_SIZE = 100;

const transactionClones = new WeakMap();

function TransactionsComponent(props) {
	const {
		editTransaction,
		categories,
		transactionCategoryEdit,
		getCategoryGroupsByType,
		transactionFinder,
		toggleShowBankAccountTransactions,
		toggleShowHiddenBankAccountTransactions,
		hideBankTransaction,
		unhideBankTransaction,
		cancelledAddAsBankTransaction,
		addBankTransactionAsTransaction,
		setTransactionToEditMode,
		transactionSubmit,
	} = props;
	const [editType, updateEditType] = useState(null);
	const transactions = useSelector(state => state.dashboard.transactions); //Just in case the transaction list gets updated, this will trigger a rerender
	const searchText = useSelector(state => state.transactions.transactionSearchText);
	const searchQuery = useSelector(state => state.transactions.transactionSearchQuery);
	const searchSort = useSelector(state => state.transactions.transactionSearchSort);
	const isSearching = searchText !== '';
	const loadingSearchResults = isSearching && searchText !== searchQuery;
	const [searchResultsShown, updateSearchResultsShown] = useState(SEARCH_PAGE_SIZE);
	const transactionFinderUpdates = useSelector(state => state.dashboard.transactionFinderUpdates);
	const selectedDates = useSelector(state => state.dashboard.selectedDates);
	const selectedDate = useSelector(state => state.dashboard.selectedDate);
	const plaidTransactionDatesUpdating = useSelector(state => state.plaid.plaidTransactionDatesUpdating);
	const plaidTransactionDatesCached = useSelector(state => state.plaid.plaidTransactionDatesCached);
	const showingHiddenBankTransactions = useSelector(state => state.plaid.showingHiddenBankTransactions);
	const showingBankAccountTransactionsWithinTransactions = useSelector(state => state.plaid.showingBankAccountTransactionsWithinTransactions);

	useEffect(() => {
		if(!showingBankAccountTransactionsWithinTransactions) {
			return;
		}
		store.dispatch(retrieveBankAccountTransactionsIfNecessary());
	}, [showingBankAccountTransactionsWithinTransactions]);

	useEffect(() => {
		updateSearchResultsShown(SEARCH_PAGE_SIZE);
	}, [searchQuery, searchSort, showingBankAccountTransactionsWithinTransactions]);


	const changeEditTypeToOne = () => {
		changeEditType('one');
	};
	const changeEditTypeToNone = () => {
		changeEditType(null);
	};

	function changeEditType(type) {
		if(type === 'one') {
			if(selectedDate) {
				setTransactionToEditMode(Transaction.create({
					where: '',
					amount: 0,
					transaction_date: selectedDate.unix(),
				}), editTransaction);
			}
		} else {
			updateEditType(type);
		}
	}

	const searchResults = useMemo(() => {
		if(!isSearching || !searchQuery) {
			return [];
		}
		const searchable = transactionFinder.getSearchableTransactions(
			!!showingBankAccountTransactionsWithinTransactions,
			!!showingHiddenBankTransactions
		);
		return searchTransactions(searchable, searchQuery, searchSort);
	}, [
		isSearching,
		searchQuery,
		searchSort,
		transactions,
		transactionFinderUpdates,
		plaidTransactionDatesCached,
		showingHiddenBankTransactions,
		showingBankAccountTransactionsWithinTransactions,
	]);

	let { transactionsToDisplay, bankTransactionsLoading } = useMemo(() => {
		if(isSearching) {
			return {
				transactionsToDisplay: searchResults.slice(0, searchResultsShown)
			};
		}
		let bankTransactionsLoading = false;

		const filter = transactionFinder.getTransactionFinderFilter()
			.setWithVisibleUserTransactions(true);

		let transactions = null;
		if(showingBankAccountTransactionsWithinTransactions && selectedDate) {
			bankTransactionsLoading = plaidTransactionsAreLoadingOnDate(selectedDates);
			filter.setWithBankTransactions(true).setOnlyBankTransactions(true);
		}

		transactions = filter.getTransactionsByDates(selectedDates);
		return { transactionsToDisplay: transactions, bankTransactionsLoading };
	}, [
		isSearching,
		searchResults,
		searchResultsShown,
		transactions,
		transactionFinderUpdates,
		selectedDates,
		selectedDate,
		plaidTransactionDatesUpdating,
		showingHiddenBankTransactions,
		showingBankAccountTransactionsWithinTransactions,
		plaidTransactionDatesCached,
	]);

	const refundMatches = useMemo(
		() => findRefunds(transactionFinder.getAllTransactions()),
		[transactions, transactionFinderUpdates]
	);

	if(!categories) {
		return null;
	}

	return (
		<div className='transactions'>
			<div className='edit_box'>
				{editType === 'one' &&
				<TransactionSummaryComponent
					editOnly={true}
					transaction={editTransaction}
					{...props}
					categories={categories}
					endTransactionEdit={changeEditTypeToNone}
				/>
				}
				{editType === null &&
				<TransactionsToolbar
					onAdd={changeEditTypeToOne}
					showingBankTransactions={!!showingBankAccountTransactionsWithinTransactions}
					showingHiddenBankTransactions={!!showingHiddenBankTransactions}
					bankTransactionsLoading={!!bankTransactionsLoading}
					onToggleBankTransactions={toggleShowBankAccountTransactions}
					onToggleHiddenBankTransactions={toggleShowHiddenBankAccountTransactions}
					searchText={isSearching ? searchText : ''}
					searchResultCount={loadingSearchResults ? null : searchResults.length}
					searchSort={searchSort}
					onSearchSortChange={(sort) => store.dispatch(setTransactionSearchSort(sort))}
				/>
				}
			</div>
			<div className='add_transaction'>
				{(!transactionFinder.hasTransactions() || loadingSearchResults) &&
				<ProgressIndeterminate />
				}
				{transactionFinder.hasTransactions() &&
				<React.Fragment>
					{(transactionsToDisplay.length === 0) &&
					<React.Fragment>
						{(loadingSearchResults || bankTransactionsLoading) &&
						<CircleLoader />
						}
						{!(loadingSearchResults || bankTransactionsLoading) &&
						<NoResults>
							{isSearching ? 'No Results Found'
								: showingBankAccountTransactionsWithinTransactions ? 'No unbudgeted bank activity this month'
								: 'No Transactions Yet'}
						</NoResults>
						}
					</React.Fragment>
					}
					{transactionsToDisplay.length > 0 &&
					<React.Fragment>
						<div className='transactions_table row'>
							<div className=''>
								{transactionsToDisplay.map((transaction) => (
										<TransactionSummaryComponent
											key={transaction.getRenderKey()}
											transaction={transaction}
											transactionHidden={transaction.hidden}
											refundMatch={refundMatches.get(transaction)}
											addBankTransactionAsTransaction={addBankTransactionAsTransaction}
											hideBankTransaction={hideBankTransaction}
											unhideBankTransaction={unhideBankTransaction}
											cancelledAddAsBankTransaction={cancelledAddAsBankTransaction}
											transactionCategoryEdit={transactionCategoryEdit}
											categories={categories}
											transactionSubmit={transactionSubmit}
											isSaving={true}
										/>
									)
								)}
							</div>
						</div>
						{isSearching && searchResults.length > transactionsToDisplay.length &&
						<div className='transactions_search_more'>
							<button
								type='button'
								className='btn-flat transactions_filter'
								onClick={() => updateSearchResultsShown(searchResultsShown + SEARCH_PAGE_SIZE)}
							>
								Show more ({searchResults.length - transactionsToDisplay.length} left)
							</button>
						</div>
						}
					</React.Fragment>
					}
				</React.Fragment>
				}

			</div>
		</div>
	);
}

function mapStateToProps(state) {
	return {
		...state.dashboard,
		...state.plaid,
		...state.settings,
	};
}

function mapDispatchToProps(dispatch) {
	return bindActionCreators({
		...dashboardActions,
		...plaidActions,
	}, dispatch);
}

export default connect(
	mapStateToProps,
	mapDispatchToProps
)(TransactionsComponent);